import { useCallback, useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { GetApplicationResponse } from '@iwoca/lapi-client/edge';
import {
    CheckBox,
    Input,
    Message,
    Select,
    Button,
    Label,
    Link,
    Spinner,
    AlertBox,
    Icon
} from '@iwoca/orion';
import classnames from 'classnames';
import { FieldError, useForm } from 'react-hook-form';
import { connect } from 'react-redux';

import {
    SET_APPLICATION_DATA,
    SET_NEW_STATE_KEY
} from '@lendingAPI/lendingAPI.actions';
import {
    usePostApplicationMutation,
    usePostStateMutation,
    usePutStateMutation,
    usePutApplicationMutation
} from '@lendingAPI/lendingAPIMutation';
import {
    fetchGetEmailAvailability,
    useGetApplicationQuery,
    useGetStateQuery
} from '@lendingAPI/lendingAPIQueries';
import { selectStep } from '@src/actions';
import { useNavigateToStep } from '@src/hooks/useNavigateToStep';
import { logSessionIdAccess } from '@src/loggers/AccessLog';
import { AppDispatch } from '@src/reducers';
import { Step } from '@src/reducers/types/reducers.types';
import { BUSINESS_INTRODUCTION_STEP_ID } from '@src/route-old';
import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';
import { getIsLoggedInCookie } from '@src/utils/Cookies';
import {
    logErrorToDataLayer,
    postToDataLayer
} from '@src/utils/GoogleTracking';

import {
    getAmount,
    serialisePutStateBody,
    serialisePostStateBody,
    serialiseApplicationBody
} from './GetStarted.helpers';
import styles from './GetStartedUK.module.css';
import { schema } from './GetStartedUK.schema';

export type TGetStartedData = {
    email: string;
    amount: string;
    loanDuration:
        | '12-months'
        | '24-months'
        | '36-months'
        | '48-months'
        | '60-months';
    loanPurpose:
        | 'stock_purchase'
        | 'pay_staff'
        | 'pay_bill'
        | 'refinancing_debt'
        | 'growing_business'
        | 'supporting_cashflow'
        | 'other'
        | 'no_specific_need';
    marketingOptIn?: boolean;
};

type TGetStartedDataError = {
    email?: FieldError;
    amount?: FieldError;
    loanPurpose?: FieldError;
    loanDuration?: FieldError;
};

type TDuplicateEmail = {
    email?: string;
    status: boolean;
};

const ACTIONS = {
    get: 'retrieving',
    put: 'updating',
    post: 'creating'
} as const;

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
    selectStep: (selectedStep: Step) => dispatch(selectStep(selectedStep)),
    setNewStateKey: (stateKey: string) =>
        dispatch({ type: SET_NEW_STATE_KEY, stateKey }),
    setApplicationData: (applicationData: GetApplicationResponse['data']) =>
        dispatch({ type: SET_APPLICATION_DATA, applicationData })
});

const GetStartedUK = ({
    selectStep,
    setNewStateKey,
    setApplicationData
}: ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isResubmit, setIsResubmit] = useState(false);
    const [duplicateEmail, setDuplicateEmail] = useState<TDuplicateEmail>({
        email: '',
        status: false
    });

    const isLoggedIn = getIsLoggedInCookie();
    const {
        state,
        isStateSuccess,
        isStateError,
        isStateRefetching,
        prefillGetStartedState
    } = useGetStateQuery();
    const {
        isApplicationError,
        isApplicationSuccess,
        isApplicationRefetching,
        prefillGetStartedApplication,
        refetchApplication
    } = useGetApplicationQuery();

    const { mutatePostState, isNewStateError } = usePostStateMutation();
    const { mutatePostApplication, isNewApplicationError } =
        usePostApplicationMutation();
    const { mutatePutState, isStateUpdateError } = usePutStateMutation();
    const { mutatePutApplication, isApplicationUpdateError } =
        usePutApplicationMutation();

    const { navigateToStep } = useNavigateToStep();

    const {
        register,
        handleSubmit,
        getValues,
        watch,
        reset,
        formState: { errors }
    } = useForm<TGetStartedData>({
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (isResubmit) return;

        // Reset prefill when the data is stale or when GET request are completed
        if (
            (!isStateRefetching && !isApplicationRefetching) ||
            (isStateSuccess && isApplicationSuccess)
        ) {
            reset({
                ...prefillGetStartedState,
                ...prefillGetStartedApplication
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isStateRefetching,
        isApplicationRefetching,
        isStateSuccess,
        isApplicationSuccess
    ]);

    useEffect(() => {
        // Access log has no repercussion to calling it with the same sessionid_access
        logSessionIdAccess();
    }, []);

    useEffect(() => {
        tracking.ampli.stepLoaded({
            'Step name': 'Apply now',
            ...getAmplitudeDefaultEventProperties()
        });
    }, []);

    const formatCurrency = useCallback(() => {
        const amount = watch('amount')?.replace(/[^0-9]+/g, '');
        if (!amount) return '';

        return parseInt(amount).toLocaleString('en-GB');
    }, [watch]);

    const getEmailAvailability = async (email: string) => {
        if (email) {
            try {
                const emailAvailability =
                    await fetchGetEmailAvailability(email);

                // Track when email is duplicated
                if (!emailAvailability) {
                    tracking.ampli.duplicateEmail({
                        ...getAmplitudeDefaultEventProperties()
                    });
                }

                return emailAvailability;
            } catch (error: unknown) {
                datadogLogs.logger.error(
                    'Fetch email availability error',
                    { email },
                    error as Error
                );
                setDuplicateEmail({ email, status: false });
            }
        }
    };

    /**
     * Add data layer request when submission succeeds.
     * Set applicationData to match current webflow implementation.
     */
    const postGoogleTrackingSuccess = (values: TGetStartedData) => {
        const applicationData = {
            event: 'submitSignupStep0',
            applicationData: {
                applicantEmailAddress: values.email,
                requestedAmount: getAmount(values.amount),
                email_marketing_opt_in: values.marketingOptIn,
                lengthOfLoan: values.loanDuration,
                purpose: values.loanPurpose
            }
        };

        postToDataLayer(applicationData);
    };

    const onError = (errors: TGetStartedDataError) => {
        // Format the new array to not include a ref property which can include user's ppi data
        const formatError = Object.entries(errors).map((error) => {
            // Split the error key and value
            const [key, value] = error;

            // De-structure the value to remove the ref property
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { ref, ...restOfErrorValue } = value;

            // return error object without ref data.
            return { [key]: restOfErrorValue };
        });

        tracking.ampli.formValidationError({
            ...getAmplitudeDefaultEventProperties(),
            fieldErrors: formatError
        });
    };

    const onSubmit = async (values: TGetStartedData) => {
        if (!isLoggedIn && duplicateEmail.status) {
            if (duplicateEmail.email !== values.email) {
                // Edge case: This will duplicate call the email_availability endpoint when user is still onBlur and clicks the submit button immediately.
                const isEmailAvailable = await getEmailAvailability(
                    values.email
                );

                if (!isEmailAvailable) return setIsLoading(false);

                setDuplicateEmail({ email: values.email, status: false });
            } else {
                return setIsLoading(false);
            }
        }

        try {
            setIsLoading(true);

            const stateBody = state
                ? serialisePutStateBody(values, state)
                : serialisePostStateBody(values);

            const applicationBody = serialiseApplicationBody(values);

            if (isLoggedIn) {
                setIsResubmit(true);
                await mutatePutState({
                    body: stateBody
                });

                await mutatePutApplication({
                    body: applicationBody
                });

                postGoogleTrackingSuccess(values);

                const refetchResponse = await refetchApplication();
                const newApplicationData = refetchResponse?.data?.data;

                if (newApplicationData) {
                    setApplicationData(newApplicationData);
                }
            } else {
                const stateData = await mutatePostState({
                    body: stateBody
                });

                const stateKey = stateData.stateKey;

                if (stateKey && applicationBody) {
                    tracking.ampli.applicationStarted({
                        ...getAmplitudeDefaultEventProperties(),
                        requestedAmount: getAmount(values.amount),
                        requestedProductType: 'credit_facility',
                        loanDuration: values.loanDuration,
                        loanPurpose: values.loanPurpose
                    });

                    await mutatePostApplication({
                        body: applicationBody,
                        stateKey
                    });

                    postGoogleTrackingSuccess(values);
                    setNewStateKey(stateKey);
                }
            }

            setIsLoading(false);
            selectStep({
                id: BUSINESS_INTRODUCTION_STEP_ID
            });

            return navigateToStep(BUSINESS_INTRODUCTION_STEP_ID);
        } catch (error: unknown) {
            if (error instanceof Error) {
                logErrorToDataLayer('Step Zero - Form submission error');

                datadogLogs.logger.error(
                    'Application creation error',
                    error as Error
                );

                tracking.ampli.applicationCreationError();
            }
            setIsLoading(false);
        }
    };

    const renderErrorMessage = (method: 'get' | 'put' | 'post') => {
        return (
            <AlertBox
                data-testid="network-error"
                variant="failure"
                className={styles.error}>
                <Icon icon="alertTriangle" className={styles.errorIcon} />
                <div>
                    <strong>Please try again</strong> - We encountered an error
                    while {ACTIONS[method]} your application.
                </div>
            </AlertBox>
        );
    };

    if (
        (isStateRefetching && isApplicationRefetching && isLoggedIn) ||
        (!isStateSuccess && !isApplicationSuccess && isLoggedIn)
    ) {
        return (
            <div data-testid="get-started-loader" className={styles.loader}>
                <Spinner className={styles.spinnerSvg} />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.header}>Let’s get started</h1>
            <h2 className={styles.subheader}>
                Applying won’t affect your credit score
            </h2>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <div className={styles.formFieldContainer}>
                    <Label className={styles.fieldLabel} htmlFor={'email'}>
                        What is your business email address?
                    </Label>
                    <div className={styles.emailContainer}>
                        <Input
                            {...register('email')}
                            data-dd-privacy="mask"
                            className={classnames(
                                styles.formField,
                                'fs-exclude'
                            )}
                            placeholder="Enter your business email"
                            id="email"
                            disabled={isLoggedIn}
                            onBlur={async () => {
                                const email = getValues('email');
                                if (duplicateEmail.email === email) return;

                                const emailAvailable = isLoggedIn
                                    ? true
                                    : await getEmailAvailability(email);

                                if (emailAvailable !== undefined) {
                                    setDuplicateEmail({
                                        email,
                                        status: !emailAvailable
                                    });
                                }
                            }}
                            error={errors.email?.message}
                        />
                        {duplicateEmail.status && !errors.email?.message && (
                            <div
                                data-testid="duplicate-email-message"
                                className={styles.duplicateEmail}>
                                <Message
                                    type="error"
                                    message="This email is taken - please "
                                />
                                <Link
                                    size="small"
                                    href="/login"
                                    rel="noopener noreferrer"
                                    className={styles.duplicateEmailLink}>
                                    log in
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.formFieldContainer}>
                    <Label className={styles.fieldLabel} htmlFor="amount">
                        How much would you like?
                    </Label>
                    <Input
                        {...register('amount')}
                        data-dd-privacy="mask"
                        id="amount"
                        value={formatCurrency()}
                        className={classnames(styles.formField, 'fs-exclude')}
                        iconLeft="pound"
                        placeholder="Up to £1,000,000"
                        error={errors.amount?.message}
                    />
                </div>
                <div className={styles.formFieldContainer}>
                    <Label className={styles.fieldLabel} htmlFor="loanDuration">
                        How long will you need the funds for?
                    </Label>
                    <Select
                        {...register('loanDuration')}
                        data-dd-privacy="mask"
                        id="loanDuration"
                        className={classnames(styles.formField, 'fs-exclude', {
                            [styles.errorInput]: Boolean(
                                errors.loanDuration?.message
                            )
                        })}
                        placeholder="Select an option"
                        error={errors.loanDuration?.message}>
                        <option value="12-months">A few months</option>
                        <option value="24-months">Next 1-2 years</option>
                        <option value="36-months">Next 3+ years</option>
                        <option value="48-months">
                            I'd like to dip in and out
                        </option>
                        <option value="60-months">
                            I'm unsure at the moment
                        </option>
                    </Select>
                </div>
                <div className={styles.formFieldContainer}>
                    <Label className={styles.fieldLabel} htmlFor="loanPurpose">
                        How will you use the funds?
                    </Label>
                    <Select
                        {...register('loanPurpose')}
                        data-dd-privacy="mask"
                        id="loanPurpose"
                        className={classnames(styles.formField, 'fs-exclude', {
                            [styles.errorInput]: Boolean(
                                errors.loanPurpose?.message
                            )
                        })}
                        placeholder="Select an option"
                        error={errors.loanPurpose?.message}>
                        <option value="stock_purchase">Purchasing stock</option>
                        <option value="pay_staff">Paying wages</option>
                        <option value="pay_bill">Paying a bill</option>
                        <option value="refinancing_debt">
                            Refinancing debt
                        </option>
                        <option value="growing_business">
                            Growing my business
                        </option>
                        <option value="supporting_cashflow">
                            Supporting cash flow
                        </option>
                        <option value="other">Other purposes</option>
                        <option value="no_specific_need">
                            No specific need
                        </option>
                    </Select>
                </div>
                <div className={styles.submitContainer}>
                    <div className={styles.checkboxContainer}>
                        <CheckBox
                            {...register('marketingOptIn')}
                            id="marketingOptInId"
                        />
                        <Label
                            className={styles.checkBoxlabel}
                            htmlFor="marketingOptInId">
                            <strong>Sign up for updates</strong> – we’ll email
                            offers and stories from other small businesses.
                        </Label>
                    </div>
                    <Button
                        className={styles.submit}
                        loading={isLoading}
                        type="submit">
                        Apply now
                    </Button>
                </div>
            </form>
            {(isStateError || isApplicationError) && renderErrorMessage('get')}
            {(isStateUpdateError || isApplicationUpdateError) &&
                renderErrorMessage('put')}
            {(isNewStateError || isNewApplicationError) &&
                renderErrorMessage('post')}
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(GetStartedUK);
